/**
 * LoginPage · Web — REPLICA EXACTA do mobile LoginScreen.js
 *
 * Design system:
 *   - Fundo dark warm (#1A1613 base)
 *   - Card: rgba(26,22,19,0.82) + border sand 18% + shadow
 *   - Logo: Mountain icon (lucide) terracota + TECTUR 4×4 sand Bebas Neue + NAVIGATION mono sand
 *   - Tabs: fundo preto 45%, activo terracota #E87A2A
 *   - Inputs: fundo preto 35% + border sand 22% + text ink F2EBDD
 *   - Botão principal: #E87A2A terracota
 *   - Google button: dark warm + G azul em quadrado branco
 *   - Footer legal: privacy · terms em sand underlined
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Mountain } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Paleta Rugged (idêntica ao rugged.js do mobile)
const C = {
  bg: '#2B2A25',     // Mesmo base do RuggedBackground mobile (cinza rato)
  bg2: '#241E19',
  bg3: '#0F0D0B',
  brand: '#E87A2A',
  sand: '#C4A275',
  sandDim: 'rgba(196,162,117,0.22)',
  sandLine: 'rgba(196,162,117,0.18)',
  ink: '#F2EBDD',
  inkDim: '#8A7A63',
  danger: '#EF4444',
  cardBg: 'rgba(26,22,19,0.82)',
  inputBg: 'rgba(0,0,0,0.35)',
  tabsBg: 'rgba(0,0,0,0.45)',
};

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [tab, setTab] = useState('login');
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ name: '', email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showLoginPwd, setShowLoginPwd] = useState(false);
  const [showRegPwd, setShowRegPwd] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    const email = (loginData.email || '').trim().toLowerCase();
    const password = (loginData.password || '').trim();
    if (!email || !password) {
      setError('Preenche todos os campos');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await login(email, password);
      toast.success('Login bem-sucedido!');
      // Igual ao login Google: todos entram no mapa; painéis próprios abrem no botão do topo.
      navigate('/dashboard', { state: { user: res?.user }, replace: true });
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 409) {
        setError(detail || 'Esta conta foi criada com Google — usa "Continuar com Google".');
      } else {
        setError(detail || 'Erro ao fazer login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!registerData.name || !registerData.email || !registerData.password) {
      setError('Preenche todos os campos');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await register(registerData.email, registerData.password, registerData.name);
      toast.success('Registo bem-sucedido!');
      navigate('/dashboard');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Erro ao registar');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div
      className="min-h-screen w-full relative overflow-hidden flex items-center justify-center px-4 py-10"
      style={{ backgroundColor: C.bg }}
      data-testid="login-page"
    >
      {/* Fundo topográfico — mesmo topo_bg.jpg do mobile RuggedBackground */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `url(${process.env.PUBLIC_URL || ''}/topo_bg.jpg)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          opacity: 0.9,
        }}
      />

      {/* Vinheta cinemática por cima */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.60) 100%)' }}
      />

      <div className="w-full max-w-md relative z-10" style={{ padding: '24px 0' }}>
        {/* TecturLogo — Mountain + TECTUR 4×4 + NAVIGATION */}
        <div className="flex flex-col items-center mb-7" data-testid="tectur-logo">
          <Mountain
            size={64}
            strokeWidth={2.5}
            color={C.brand}
            style={{ marginBottom: 8 }}
          />
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
            <span
              style={{
                fontFamily: "'Bebas Neue', sans-serif",
                fontSize: 52,
                fontWeight: 900,
                color: C.sand,
                letterSpacing: 4,
                lineHeight: 1,
              }}
            >
              TECTUR
            </span>
            <span
              style={{
                fontFamily: "'Roboto Mono', monospace",
                fontSize: 32,
                fontWeight: 900,
                color: C.brand,
                letterSpacing: 1,
                lineHeight: 1,
              }}
            >
              4×4
            </span>
          </div>
          <div
            style={{
              fontFamily: "'Roboto Mono', monospace",
              fontSize: 12,
              letterSpacing: 5,
              color: C.sand,
              marginTop: 6,
              fontWeight: 600,
            }}
          >
            NAVIGATION
          </div>
        </div>

        {/* Card */}
        <div
          style={{
            backgroundColor: C.cardBg,
            borderRadius: 16,
            padding: 22,
            border: `1px solid ${C.sandLine}`,
            boxShadow: '0 8px 30px rgba(0,0,0,0.4)',
          }}
          data-testid="auth-card"
        >
          <h2
            style={{
              fontSize: 24, fontWeight: 700,
              color: C.ink, textAlign: 'center', margin: 0,
            }}
          >
            {tab === 'login' ? 'Bem-vindo' : 'Criar conta'}
          </h2>
          <p style={{ fontSize: 14, color: C.sand, textAlign: 'center', margin: '4px 0 20px' }}>
            {tab === 'login' ? 'Entra na tua conta Tectur' : 'Regista-te para começar a explorar'}
          </p>

          {/* Tabs pill */}
          <div
            style={{
              display: 'flex',
              backgroundColor: C.tabsBg,
              borderRadius: 10,
              padding: 4,
              marginBottom: 20,
              border: '1px solid rgba(196,162,117,0.14)',
            }}
          >
            <TabBtn active={tab === 'login'} onClick={() => { setTab('login'); setError(null); }} testid="login-tab">
              Entrar
            </TabBtn>
            <TabBtn active={tab === 'register'} onClick={() => { setTab('register'); setError(null); }} testid="register-tab">
              Registar
            </TabBtn>
          </div>

          {tab === 'login' ? (
            <form onSubmit={handleLogin}>
              <Label>Email</Label>
              <RuggedInput
                type="email"
                placeholder="seu@email.com"
                value={loginData.email}
                onChange={(v) => setLoginData({ ...loginData, email: v })}
                testid="login-email-input"
                autoComplete="email"
              />
              <Label>Password</Label>
              <PasswordInput
                value={loginData.password}
                onChange={(v) => setLoginData({ ...loginData, password: v })}
                show={showLoginPwd}
                onToggle={() => setShowLoginPwd((s) => !s)}
                testid="login-password-input"
                toggleTestid="toggle-login-pwd"
              />
              {error && <ErrorText>{error}</ErrorText>}
              <PrimaryBtn disabled={loading} testid="login-submit-button">
                {loading ? 'A entrar...' : 'Entrar'}
              </PrimaryBtn>

              <div style={{ marginTop: 14, textAlign: 'center' }}>
                <a
                  href="/forgot-password"
                  onClick={(e) => { e.preventDefault(); navigate('/forgot-password'); }}
                  style={{ color: C.brand, fontSize: 13, fontWeight: 600, textDecoration: 'underline' }}
                  data-testid="forgot-password-link"
                >
                  Esqueci-me da password
                </a>
              </div>

              <Divider>OU</Divider>

              <GoogleBtn disabled={loading} onClick={handleGoogleLogin} testid="google-login-button">
                Continuar com Google
              </GoogleBtn>
            </form>
          ) : (
            <form onSubmit={handleRegister}>
              <Label>Nome</Label>
              <RuggedInput
                type="text"
                placeholder="O teu nome"
                value={registerData.name}
                onChange={(v) => setRegisterData({ ...registerData, name: v })}
                testid="register-name-input"
              />
              <Label>Email</Label>
              <RuggedInput
                type="email"
                placeholder="seu@email.com"
                value={registerData.email}
                onChange={(v) => setRegisterData({ ...registerData, email: v })}
                testid="register-email-input"
                autoComplete="email"
              />
              <Label>Password</Label>
              <PasswordInput
                value={registerData.password}
                onChange={(v) => setRegisterData({ ...registerData, password: v })}
                show={showRegPwd}
                onToggle={() => setShowRegPwd((s) => !s)}
                testid="register-password-input"
                toggleTestid="toggle-register-pwd"
              />
              {error && <ErrorText>{error}</ErrorText>}
              <PrimaryBtn disabled={loading} testid="register-submit-button">
                {loading ? 'A registar...' : 'Criar conta'}
              </PrimaryBtn>

              <Divider>OU</Divider>

              <GoogleBtn disabled={loading} onClick={handleGoogleLogin} testid="google-register-button">
                Registar com Google
              </GoogleBtn>
            </form>
          )}
        </div>

        {/* Footer tagline */}
        <p style={{ textAlign: 'center', fontSize: 13, color: C.sand, marginTop: 22 }}>
          Explore rotas 4×4 com a Tectur
        </p>

        {/* Legal */}
        <div style={{ textAlign: 'center', marginTop: 12, fontSize: 12 }}>
          <a
            href={`${BACKEND_URL}/api/legal/privacy`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: C.sand, textDecoration: 'underline' }}
            data-testid="login-privacy-link"
          >
            Política de Privacidade
          </a>
          <span style={{ color: C.sand, margin: '0 8px' }}>·</span>
          <a
            href={`${BACKEND_URL}/api/legal/terms`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: C.sand, textDecoration: 'underline' }}
            data-testid="login-terms-link"
          >
            Termos e Condições
          </a>
        </div>
      </div>
    </div>
  );
};

// ============= Sub-componentes internos (mesmo styling do mobile) =============

const TabBtn = ({ active, onClick, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    style={{
      flex: 1,
      padding: '9px 0',
      borderRadius: 8,
      background: active ? C.brand : 'transparent',
      color: active ? '#fff' : C.sand,
      fontSize: 14,
      fontWeight: active ? 700 : 500,
      border: 'none',
      cursor: 'pointer',
      boxShadow: active ? '0 1px 4px rgba(0,0,0,0.2)' : 'none',
    }}
  >
    {children}
  </button>
);

const Label = ({ children }) => (
  <div style={{ fontSize: 13, color: C.ink, marginBottom: 6, marginTop: 14, fontWeight: 500 }}>
    {children}
  </div>
);

const RuggedInput = ({ type, placeholder, value, onChange, testid, autoComplete }) => (
  <input
    type={type}
    placeholder={placeholder}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    autoComplete={autoComplete}
    autoCapitalize="off"
    autoCorrect="off"
    data-testid={testid}
    style={{
      width: '100%',
      background: C.inputBg,
      color: C.ink,
      border: `1px solid ${C.sandDim}`,
      borderRadius: 10,
      padding: '11px 14px',
      fontSize: 15,
      outline: 'none',
      boxSizing: 'border-box',
    }}
    onFocus={(e) => (e.target.style.borderColor = C.brand)}
    onBlur={(e) => (e.target.style.borderColor = C.sandDim)}
  />
);

const PasswordInput = ({ value, onChange, show, onToggle, testid, toggleTestid }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      background: C.inputBg,
      border: `1px solid ${C.sandDim}`,
      borderRadius: 10,
    }}
  >
    <input
      type={show ? 'text' : 'password'}
      placeholder="••••••••"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      autoComplete="current-password"
      data-testid={testid}
      style={{
        flex: 1,
        background: 'transparent',
        color: C.ink,
        border: 'none',
        padding: '11px 14px',
        fontSize: 15,
        outline: 'none',
      }}
    />
    <button
      type="button"
      onClick={onToggle}
      data-testid={toggleTestid}
      style={{
        background: 'transparent',
        border: 'none',
        padding: '0 14px',
        fontSize: 18,
        color: C.sand,
        cursor: 'pointer',
      }}
    >
      {show ? '🙈' : '👁'}
    </button>
  </div>
);

const ErrorText = ({ children }) => (
  <div style={{ color: C.danger, fontSize: 13, marginTop: 14, textAlign: 'center' }}>{children}</div>
);

const PrimaryBtn = ({ children, disabled, testid }) => (
  <button
    type="submit"
    disabled={disabled}
    data-testid={testid}
    style={{
      width: '100%',
      background: C.brand,
      color: '#fff',
      fontWeight: 700,
      fontSize: 16,
      letterSpacing: 0.3,
      padding: '14px 0',
      borderRadius: 10,
      border: 'none',
      cursor: disabled ? 'not-allowed' : 'pointer',
      marginTop: 22,
      opacity: disabled ? 0.6 : 1,
    }}
  >
    {children}
  </button>
);

const Divider = ({ children }) => (
  <div style={{ display: 'flex', alignItems: 'center', marginTop: 22, marginBottom: 14 }}>
    <div style={{ flex: 1, height: 1, background: '#2E2620' }} />
    <span style={{ margin: '0 12px', fontSize: 11, color: C.sand, fontWeight: 700, letterSpacing: 2 }}>
      {children}
    </span>
    <div style={{ flex: 1, height: 1, background: '#2E2620' }} />
  </div>
);

const GoogleBtn = ({ children, disabled, onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    data-testid={testid}
    style={{
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 10,
      background: 'rgba(26,22,19,0.85)',
      color: C.ink,
      border: '1.5px solid rgba(196,162,117,0.28)',
      borderRadius: 10,
      padding: '12px 0',
      fontWeight: 700,
      fontSize: 15,
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.6 : 1,
    }}
  >
    <span
      style={{
        display: 'inline-grid',
        placeItems: 'center',
        width: 22,
        height: 22,
        background: '#fff',
        color: '#4285F4',
        fontWeight: 900,
        fontSize: 15,
        borderRadius: 3,
        border: '1px solid rgba(237,228,211,0.10)',
      }}
    >
      G
    </span>
    {children}
  </button>
);

export default LoginPage;
